<template>
	<ParamWrapper v-bind="wrapperProps" @input-data-closed="onDataInputMenuClose">
		<MultiSelectTabs
			v-if="localTab"
			:localTab="localTab"
			:hidePredefinedOptions="!hasSourceOrOptions"
			@change-tab="changeTab"
			@choose-from-predefined-options="chooseFromPredefinedOptions"
		/>
		<component :is="component" v-bind="componentProps" v-on="componentListeners" />
		<template v-slot:input-data>
			<slot name="input-data">
				<SelectDataInputMenu
					:param="param"
					:items="selectItems"
					ref="data-input-menu"
					v-on="dataInputListeners"
					@update-external-localValue="updateSelectValue"
				/>
			</slot>
		</template>
	</ParamWrapper>
</template>

<script>
import ExternalSourceMixin from './mixins/ExternalSourceMixin';
import ParamTypeExtenderDataInputMenuMixin from './mixins/ParamTypeExtenderDataInputMenuMixin';
import ParamTypeMixin from './mixins/ParamTypeMixin';
import SelectItemsMixin from './mixins/SelectItemsMixin';
import ThisFlowMixin from './mixins/ThisFlowMixin';
import ParamTypeHelper from './param-type-helper';
import { SELECT_TAB_KEY } from '../../../DataInput/DataInputMenu/helpers/data-input-menu.const';
import SelectDataInputMenu from '../DataInputMenus/SelectDataInputMenu';
import DefaultPropertyDropdownInput from '../inputsComponents/DefaultPropertyDropdownInput';
import MultiSelectManualOptions from '../inputsComponents/MultiSelectManualOptions';
import MultiSelectTabs from '@/components/BlockEditor/BlockEditorParams/ParamTypes/common/MultiSelectTabs';
import MultiSelectColumnSelection from '@/components/BlockEditor/BlockEditorParams/inputsComponents/MultiSelectColumnSelection';

export default {
	name: 'MultipleSelectParamType',
	mixins: [ParamTypeMixin, ParamTypeExtenderDataInputMenuMixin, ThisFlowMixin, ExternalSourceMixin, SelectItemsMixin],
	components: {
		MultiSelectTabs,
		SelectDataInputMenu,
		MultiSelectManualOptions,
		MultiSelectColumnSelection,
		DefaultPropertyDropdownInput,
	},
	computed: {
		hasSourceOrOptions() {
			return ['options', 'source'].some((paramField) => paramField in this.param);
		},
		fromSelectOptions() {
			return this.curTabCode === SELECT_TAB_KEY;
		},
		componentConfig() {
			const EXTERNAL_VALUE_PROPS = {
				...this.$attrs,
				ref: 'richEditor',
				rules: this.fieldRules,
				multipleSelection: true,
				readonly: this.readonly,
				paramKey: this.param.key,
				isFocused: this.isFocused,
				value: this.localValueDisplay,
				showDataMenu: this.showDataMenu,
				isDropdownOpen: this.showDataMenu,
				inputFieldHint: this.param.placeholder,
			};
			const RICH_TEXT_BOX_PROPS = {
				...this.$attrs,
				node: this.node,
				ref: 'richEditor',
				param: this.param,
				action: this.action,
				service: this.service,
				value: this.localValue,
				readonly: this.readonly,
				multipleSelection: true,
				isFocused: this.isFocused,
				tableSource: this.tableSource,
				showDataMenu: this.showDataMenu,
				selectedColumn: this.selectedColumn,
				inputFieldHint: this.param.placeholder,
				options: this.paramValueInParamsMap.value,
			};
			const EXTERNAL_VALUE_LISTENERS = {
				blur: this.onInputBlur,
				focus: this.onInputFocus,
				click: this.onInputClick,
				open: this.openDataInputMenu,
				close: this.closeDataInputMenu,
				'clear-value': this.clearValue,
				'update-params-map': this.updateParamsMapKey,
			};
			const RICH_TEXT_BOX_LISTENERS = {
				blur: this.onInputBlur,
				focus: this.onInputFocus,
				click: this.onInputClick,
				open: this.openDataInputMenu,
				close: this.closeDataInputMenu,
				input: (val) => (this.localValue = val),
				'update-params-map': this.updateParamsMapKey,
			};
			const MANUAL_MULTI_SELECT_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
				MultiSelectManualOptions,
				RICH_TEXT_BOX_PROPS,
				RICH_TEXT_BOX_LISTENERS
			);
			const COLUMN_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
				MultiSelectColumnSelection,
				RICH_TEXT_BOX_PROPS,
				RICH_TEXT_BOX_LISTENERS
			);

			const REGULAR_PROPERTY_DROPDOWN_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
				DefaultPropertyDropdownInput,
				EXTERNAL_VALUE_PROPS,
				EXTERNAL_VALUE_LISTENERS
			);

			return this.localTab
				? this.localTab === 'manual'
					? MANUAL_MULTI_SELECT_INPUT_CONFIG
					: COLUMN_INPUT_CONFIG
				: REGULAR_PROPERTY_DROPDOWN_INPUT_CONFIG;
		},
		component() {
			return this.componentConfig.component;
		},
		componentProps() {
			return this.componentConfig.props;
		},
		componentListeners() {
			return this.componentConfig.listeners;
		},
		paramValueInParamsMap() {
			return this.paramsMap[this.param.key] || {};
		},
		tableSource() {
			return this.paramValueInParamsMap.table_source;
		},
		selectedColumn() {
			return this.paramValueInParamsMap.column_name;
		},
	},

	methods: {
		changeTab(newV) {
			if (newV !== this.localTab) {
				this.localTab = newV;
				this.updateParamsMapKey(null, true);
			}
		},
		addSelectTabItem() {
			const fieldName = this.param.name ?? 'fieldName';
			this.addTabItem(SELECT_TAB_KEY, fieldName, true);
		},
		clearValue() {
			this.updateSelectValue({ id: '', name: '' });
		},
		updateSelectValue(payload) {
			this.updateConstantValueFromDataInputMenu(payload);
		},
		updateParamsMapKey(value) {
			this.localValue = !this.localTab ? undefined : { multi_select_type: this.localTab, ...value };
		},
		initializeStateByParamsMapValue(defaultValueToSet) {
			if (defaultValueToSet) {
				this.localTab = defaultValueToSet;
			} else if (this.paramValueInParamsMap.multi_select_type) {
				this.localTab = this.paramValueInParamsMap.multi_select_type;
			} else {
				this.localTab = this.hasSourceOrOptions ? null : 'manual';
			}
		},
		chooseFromPredefinedOptions() {
			this.setTab(0);
			this.localTab = null;
			this.updateParamsMapKey(null, true);
		},
	},
	mounted() {
		this.addSelectTabItem();
		this.initializeStateByParamsMapValue();
	},
	data() {
		return {
			localTab: null,
		};
	},
	watch: {
		componentConfig: {
			handler() {
				this.$nextTick(() => {
					this.setInputElement(this.$refs['richEditor']?.$el);
				});
			},
		},
		fromSelectOptions(newV) {
			if (!newV && !this.localTab) {
				this.initializeStateByParamsMapValue('manual');
			}
		},
	},
};
</script>
<style lang="scss" scoped></style>
